/**
 * The homepage catalogue section.
 *
 * This file used to be three things at once: a hover-driven tab switch, a
 * 28-tile grid of hardcoded dataset names that opened an overlay with no URL,
 * and an image gallery that fetched PNGs from the GitHub API at runtime. All
 * three are gone.
 *
 * What replaces them is the actual catalogue, rendered from the dataset
 * registry (`app/data/datasets`) with the same components the /datasets index
 * uses — `DatasetTable` and `DatasetRows`. There is one source of truth for the
 * catalogue and one set of components rendering it, so the homepage cannot
 * drift from the catalogue page.
 *
 * Every dataset name is a `<Link>` to `/datasets/<slug>` — a real, linkable,
 * back-button-able page — because that is what those components already do.
 */
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { catalogueStats, datasets } from 'app/data/datasets';
import DatasetTable, {
  SortKey,
  SortState,
  sortDatasets,
} from 'app/pages/Datasets/DatasetTable';
import DatasetRows from 'app/pages/Datasets/DatasetRows';

/** Name ascending: the scannable default for a catalogue. */
const DEFAULT_SORT: SortState = { key: 'name', direction: 'asc' };

/** Magnitude columns read better starting descending — biggest first. */
const DESCENDING_FIRST: SortKey[] = ['entities'];

/**
 * Below this width the seven-column table would have to scroll sideways to show
 * anything past the name, so the row-block view is rendered instead. Only one of
 * the two is ever in the DOM — rendering both and hiding one with CSS would put
 * 27 duplicate links in front of a screen reader.
 */
const TABLE_MIN_WIDTH = '(min-width: 768px)';

function useWideViewport() {
  const [wide, setWide] = useState(
    () =>
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia(TABLE_MIN_WIDTH).matches,
  );

  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      typeof window.matchMedia !== 'function'
    )
      return;
    const query = window.matchMedia(TABLE_MIN_WIDTH);
    const onChange = (event: MediaQueryListEvent) => setWide(event.matches);
    setWide(query.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  return wide;
}

/**
 * `pip install sovai` — a plain bordered mono line with a copy control.
 * No blinking caret, no perpetual gradient, no tooltip theatre.
 */
const InstallLine = memo(() => {
  const [copied, setCopied] = useState(false);
  const command = 'pip install sovai';

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(command);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy command:', error);
    }
  }, []);

  return (
    <div className="flex items-center border border-gray-800 font-mono text-sm">
      <code className="px-3 py-2 text-gray-300">
        <span className="text-gray-400">$ </span>
        pip install <span className="text-blue-400">sovai</span>
      </code>
      <button
        type="button"
        onClick={handleCopy}
        className="border-l border-gray-800 px-3 py-2 text-[11px] uppercase tracking-wider text-gray-400 hover:text-blue-300 focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-blue-400"
      >
        {copied ? 'copied' : 'copy'}
      </button>
      <span className="sr-only" role="status">
        {copied ? 'Command copied to clipboard' : ''}
      </span>
    </div>
  );
});

InstallLine.displayName = 'InstallLine';

/**
 * Wayfinding into the three documentation surfaces the removed feature cards
 * used to link to. The old cards carried the links but described them with
 * unfalsifiable copy ("Gain an unbeatable edge…"); these lines describe what the
 * docs sections actually contain and nothing more. If a capability's scope is
 * not established by its documentation, the label carries it and the link does
 * the rest — a bare accurate label beats a confident wrong description.
 */
const CAPABILITIES: Array<{ label: string; detail: string; href: string }> = [
  {
    label: 'realtime datasets',
    detail: 'The catalogue above, with per-dataset schemas and query examples.',
    href: 'https://docs.sov.ai/realtime-datasets/',
  },
  {
    label: 'pattern recognition',
    detail:
      'Pairwise distance, anomaly detection and clustering over a dataset.',
    href: 'https://docs.sov.ai/pattern-recognition',
  },
  {
    label: 'feature processing',
    detail:
      'Feature extraction, neutralisation, selection, dimensionality reduction and importance.',
    href: 'https://docs.sov.ai/feature-processing/',
  },
];

const CapabilityStrip = memo(() => (
  <div className="relative border border-gray-800">
    <span className="absolute -top-[7px] left-3 bg-[#0d1117] px-1.5 font-mono text-[11px] uppercase tracking-wider text-gray-400">
      documentation
    </span>
    <ul className="pt-1">
      {CAPABILITIES.map(capability => (
        <li
          key={capability.href}
          className="border-b border-dashed border-gray-800/70 last:border-b-0"
        >
          <a
            href={capability.href}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col gap-x-4 gap-y-0.5 px-3 py-2 no-underline hover:bg-gray-900/60 focus-visible:outline focus-visible:outline-1 focus-visible:-outline-offset-1 focus-visible:outline-blue-400 sm:flex-row sm:items-baseline"
          >
            <span className="w-48 shrink-0 font-mono text-xs text-white">
              {capability.label}
            </span>
            <span className="text-xs text-gray-400">{capability.detail}</span>
            <span
              aria-hidden="true"
              className="ml-auto shrink-0 font-mono text-[11px] text-gray-400"
            >
              docs.sov.ai ↗
            </span>
          </a>
        </li>
      ))}
    </ul>
  </div>
));

CapabilityStrip.displayName = 'CapabilityStrip';

const OptimizedAnimationBox = memo(() => {
  const stats = useMemo(() => catalogueStats(), []);
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
  const wide = useWideViewport();

  const handleSortChange = useCallback((key: SortKey) => {
    setSort(current =>
      current.key === key
        ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: DESCENDING_FIRST.includes(key) ? 'desc' : 'asc' },
    );
  }, []);

  // Pure and over 27 rows — instant, no memo gymnastics required beyond this.
  const rows = useMemo(() => sortDatasets(datasets, sort), [sort]);

  return (
    <section
      // `features` already belongs to the hero above; a duplicate id would make
      // the in-page anchor ambiguous.
      id="catalogue"
      aria-labelledby="catalogue-heading"
      className="bg-[#0d1117] px-4 py-10 sm:px-6 lg:px-8"
    >
      <div className="mx-auto max-w-7xl">
        <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <h2
              id="catalogue-heading"
              className="font-mono text-xl font-semibold text-white md:text-2xl"
            >
              The catalogue
            </h2>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-gray-400">
              All{' '}
              <span className="font-mono tabular-nums text-gray-200">
                {stats.total}
              </span>{' '}
              datasets, with the coverage metadata we have verified. Fields we
              have not published read{' '}
              <span className="font-mono text-xs text-gray-400">
                Not yet documented
              </span>{' '}
              rather than estimated.
            </p>
          </div>
          <InstallLine />
        </div>

        {wide ? (
          <DatasetTable
            rows={rows}
            sort={sort}
            onSortChange={handleSortChange}
          />
        ) : (
          <DatasetRows rows={rows} />
        )}

        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 font-mono text-[11px] text-gray-400">
          <span>
            <span className="tabular-nums text-gray-300">{stats.total}</span>{' '}
            datasets · {stats.categories.equity} equity /{' '}
            {stats.categories.economic} economic / {stats.categories.sectorial}{' '}
            sectorial
          </span>
          <Link
            to="/datasets"
            className="text-blue-400 underline underline-offset-2 hover:text-blue-300 focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-blue-400"
          >
            Filter and sort the full catalogue →
          </Link>
        </div>

        <div className="mt-8">
          <CapabilityStrip />
        </div>
      </div>
    </section>
  );
});

OptimizedAnimationBox.displayName = 'OptimizedAnimationBox';

export default OptimizedAnimationBox;
